import { useMemo } from 'react'
import { useThemeOptional } from './useTheme'

/**
 * Chart colours that follow the theme — VH-133
 *
 * recharts takes its colours as JS props (`stroke`, `fill`, `tick`), not as CSS
 * classes, so it inherits nothing from the design system. Eight chart files
 * would otherwise each hardcode a palette, twice — once per theme — and drift
 * apart the first time one of them is edited.
 *
 * Values are read from the computed CSS custom properties rather than
 * duplicated here, so `styles/index.css` stays the single definition of every
 * colour in the app. The dependency on the resolved theme is what re-reads
 * them: the hook recomputes when the palette flips, which is exactly when
 * recharts needs new props to re-render with.
 *
 * The literals below are fallbacks for environments with no real layout engine
 * — jsdom returns an empty string for a custom property it has not cascaded —
 * so charts render in tests instead of throwing on `undefined`.
 */

const FALLBACKS = {
  light: {
    axis: '#414d4e',
    grid: '#e0e4e4',
    surface: '#ffffff',
    text: '#1a1c1c',
    accent: '#00727d',
    protein: '#c62828',
    carbs: '#4f6b00',
    fat: '#0277bd',
    primary: '#00727d',
    danger: '#dc2626',
    warning: '#d97706',
    success: '#15803d',
    info: '#2563eb',
  },
  // Sharp Technical (VH-135) — keep in step with the `.dark` block in index.css.
  dark: {
    axis: '#cbc3d7',
    grid: '#494454',
    surface: '#1d1928',
    text: '#e7dff5',
    accent: '#4edea3',
    protein: '#ff5e5e',
    carbs: '#c3f400',
    fat: '#a3defe',
    primary: '#d0bcff',
    danger: '#ffb2b7',
    warning: '#f5c34a',
    success: '#4edea3',
    info: '#93c5fd',
  },
}

// Which custom property backs each role. Keep in step with :root / .dark.
const VARIABLES = {
  axis: '--vh-on-surface-variant',
  grid: '--vh-outline-variant',
  surface: '--vh-surface-container',
  text: '--vh-on-surface',
  accent: '--vh-accent',
  protein: '--vh-protein',
  carbs: '--vh-carbs',
  fat: '--vh-fat',
  // VH-135 — series colours. VH-133 themed the chart *chrome* (axes, grid,
  // tooltip) but left every `<Line stroke="#4F46E5">` literal in place. That
  // was invisible on charcoal and is not on deep purple: the amber calories
  // fill at 0.22 opacity turned muddy brown, and indigo dropped to near the
  // canvas luminance. Series colours have to come from the palette too.
  primary: '--vh-primary-600',
  danger: '--vh-danger-600',
  warning: '--vh-warning-600',
  success: '--vh-success-600',
  info: '--vh-blue-600',
}

const readVariables = (theme) => {
  const fallback = FALLBACKS[theme] ?? FALLBACKS.light

  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback
  }

  const styles = window.getComputedStyle(document.documentElement)
  const resolve = (role) => {
    const value = styles.getPropertyValue(VARIABLES[role])?.trim()
    return value || fallback[role]
  }

  return Object.fromEntries(
    Object.keys(VARIABLES).map((role) => [role, resolve(role)])
  )
}

/**
 * Which palette is on screen, without requiring the provider.
 *
 * A chart is a passive consumer of the theme — it never sets one — so it must
 * render outside the app shell too. The root element carries `data-theme`
 * (stamped by the pre-paint script and kept current by ThemeProvider), which
 * is the same answer the context would give.
 */
const currentTheme = (fromContext) => {
  if (fromContext) return fromContext
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light'
}

export const useChartTheme = () => {
  const theme = currentTheme(useThemeOptional()?.theme)

  return useMemo(() => {
    const colors = readVariables(theme)

    return {
      ...colors,
      theme,
      /** The macro trio, in the order they are shown throughout the app. */
      macros: [colors.protein, colors.carbs, colors.fat],
      /**
       * Categorical series colours, for charts whose series carry no inherent
       * meaning (pie slices, one line per exercise). Ordered for maximum
       * separation between neighbours rather than by hue, since adjacent
       * entries are what a reader has to tell apart.
       *
       * `primary` and `success` are deliberately absent. Each collides with
       * `accent` in one theme — `--vh-primary-600` IS `--vh-accent` in light
       * (both #00727d), and `--vh-success-600` IS `--vh-accent` in dark (both
       * #4edea3, which VH-135 chose on purpose). Either would give two
       * identical slices in one theme while looking fine in the other. Use them
       * by name where they carry meaning; not here, where they carry none.
       */
      series: [
        colors.accent,
        colors.danger,
        colors.warning,
        colors.info,
        colors.carbs,
        colors.fat,
      ],
      /** Spread onto recharts <XAxis>/<YAxis> so axes are legible in both. */
      axisProps: {
        stroke: colors.axis,
        tick: { fill: colors.axis, fontSize: 12 },
      },
      /** Spread onto <CartesianGrid>. */
      gridProps: {
        stroke: colors.grid,
        strokeDasharray: '3 3',
      },
      /** Spread onto <Tooltip> — the default is a hardcoded white card. */
      tooltipProps: {
        contentStyle: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.grid}`,
          borderRadius: '0.75rem',
          color: colors.text,
        },
        labelStyle: { color: colors.text },
        itemStyle: { color: colors.text },
      },
    }
  }, [theme])
}

export default useChartTheme
